import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { StyledContainer } from '../../dynamicform/styled.js';
import { validateAuth, getApiBaseUrl, formatUtcDate } from '../../util/util';
import {
  CONTACT_NAME_MAX,
  MOJ_REQUEST_DATE_HELP,
  MOJ_SUPPLIER_ID_HELP,
} from '../../util/constants';
import { isFeaturesEnabled } from '../../util/featureConfig';
import axios from 'axios';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { userTypeSelector } from '../../redux/selectors';

/**
 * UserDetails displays the logged-in external user's profile information,
 * MOJ status, induction status and datacentre permissions from the permission API.
 */
const UserDetails = () => {
  const currentUserType = useSelector(userTypeSelector);
  const featuresEnabled = isFeaturesEnabled(currentUserType.type);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [inductions, setInductions] = useState([]);
  const [mojRecords, setMojRecords] = useState([]);

  // MOJ editing state
  const [mojRequestDate, setMojRequestDate] = useState(null);
  const [mojSupplierId, setMojSupplierId] = useState('');
  const [mojSaving, setMojSaving] = useState(false);
  const [mojSuccess, setMojSuccess] = useState('');
  const [mojError, setMojError] = useState('');
  const [isApprover, setIsApprover] = useState(false);
  const [aalList, setAalList] = useState([]);

  // Profile editing
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [expandedAalRow, setExpandedAalRow] = useState(null);

  useEffect(() => {
    fetchUserDetails();
    if (featuresEnabled) fetchAalList();
  }, []);

  const fetchAalList = async () => {
    try {
      await validateAuth(async (session) => {
        const resp = await axios.get(
          `${getApiBaseUrl(currentUserType.type)}/contacts/self/aal_list`,
          {
            headers: { Authorization: session.getIdToken().getJwtToken() },
            timeout: 15000,
          }
        );
        setAalList(Array.isArray(resp.data?.data) ? resp.data.data : []);
      });
    } catch {
      // silent
    }
  };

  const startProfileEdit = () => {
    setProfileForm({
      name: userData?.name || '',
      employer: userData?.employer || '',
      role: userData?.role || '',
      phone_number: userData?.phone_number || '',
      mobile_number: userData?.mobile_number || '',
    });
    setProfileError('');
    setProfileSuccess('');
    setProfileEditing(true);
  };

  const handleProfileSave = async () => {
    if (!profileForm.name?.trim()) {
      setProfileError('Name is required');
      return;
    }
    // Portal users sign in with their mobile, so it can be changed but not removed
    if (userData?.mobile_number && !profileForm.mobile_number?.trim()) {
      setProfileError('Mobile is required for App/Portal Users and cannot be removed.');
      return;
    }
    setProfileSaving(true);
    setProfileError('');
    try {
      await validateAuth(async (session) => {
        await axios.patch(`${getApiBaseUrl(currentUserType.type)}/contacts/self`, profileForm, {
          headers: { Authorization: session.getIdToken().getJwtToken() },
          timeout: 15000,
        });
      });
      setProfileEditing(false);
      setProfileSuccess('Profile updated.');
      fetchUserDetails();
    } catch (err) {
      setProfileError(err?.response?.data?.errors?.join?.(', ') || 'Failed to save profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    setError('');
    try {
      await validateAuth(async (session) => {
        const response = await axios.get(
          `${process.env.REACT_APP_AWS_API_SHARED_URL}/contacts/permission`,
          {
            headers: {
              Authorization: session.getIdToken().getJwtToken(),
              'Content-Type': 'application/json',
            },
            timeout: 45 * 1000,
          }
        );
        setUserData(response.data.user || null);
        setPermissions(response.data.permissions || []);
        setIsApprover(!!response.data.approver);

        // MOJ records array
        const rawMoj = response.data.moj;
        setMojRecords(Array.isArray(rawMoj) ? rawMoj : rawMoj ? [rawMoj] : []);

        // Initialize MOJ fields from user data
        const user = response.data.user;
        if (user?.requested_date) {
          setMojRequestDate(moment(formatUtcDate(user.requested_date, 'YYYY-MM-DD')));
        }
        setMojSupplierId(user?.moj_supplier_id || '');

        // Normalise induction to an array (API may return a single object or an array)
        const rawInduction = response.data.induction;
        if (Array.isArray(rawInduction)) {
          setInductions(rawInduction);
        } else if (rawInduction) {
          setInductions([rawInduction]);
        } else {
          setInductions([]);
        }
      });
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Unable to load user details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMojSave = async () => {
    const hadMoj = !!(userData?.moj_supplier_id || userData?.requested_date);
    if (hadMoj && !mojSupplierId.trim() && !mojRequestDate) {
      setMojError(
        'Ministry of Justice details cannot be removed once recorded. Update them with the latest Criminal Background Check (CBC) instead.'
      );
      return;
    }
    if (!mojSupplierId.trim()) {
      setMojError('Ministry of Justice Supplier Request ID is required.');
      return;
    }
    if (!mojRequestDate) {
      setMojError('Ministry of Justice Request Date is required.');
      return;
    }
    setMojSaving(true);
    setMojError('');
    setMojSuccess('');
    // Validation: prevent saving if existing expiry is in the past
    if (expiryIsPast) {
      setMojError(
        'Ministry of Justice Request Date cannot be more than two years ago. The Criminal Background Check (CBC) must have been completed within the last two years.'
      );
      setMojSaving(false);
      return;
    }
    // Validation: prevent saving a start date in the future (covers typed values)
    if (requestDateIsFuture) {
      setMojError('Ministry of Justice Request Date cannot be in the future.');
      setMojSaving(false);
      return;
    }
    try {
      await validateAuth(async (session) => {
        await axios.patch(
          `${getApiBaseUrl(currentUserType.type)}/contacts/moj_details/${userData.id}`,
          {
            mojStartDate: mojRequestDate ? mojRequestDate.toISOString() : null,
            mojSupplierID: mojSupplierId,
            platform: 'Customer Portal',
          },
          {
            headers: {
              Authorization: session.getIdToken().getJwtToken(),
              'Content-Type': 'application/json',
            },
            timeout: 30 * 1000,
          }
        );
        setMojSuccess(
          'Ministry of Justice details updated successfully. The status will be re-evaluated shortly based on the new date.'
        );
        // Refresh user data to reflect the change
        fetchUserDetails();
      });
    } catch (err) {
      console.error('Error updating MOJ details:', err);
      setMojError('Failed to update Ministry of Justice details. Please try again.');
    } finally {
      setMojSaving(false);
    }
  };

  const getMojExpiryDate = () => {
    if (!mojRequestDate || !mojRequestDate.isValid()) return null;
    return moment(mojRequestDate).add(2, 'years');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return moment(dateStr).format('DD MMM YYYY, h:mm A');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No user details available.</Alert>
      </Box>
    );
  }

  const mojValid = !!userData.moj_supplier_id;

  // Derive MOJ status from the moj records array
  const latestMoj = mojRecords.length > 0 ? mojRecords[mojRecords.length - 1] : null;
  const mojStatus = latestMoj?.status || null;
  const mojExpiry = latestMoj?.moj_expiry_date
    ? moment(formatUtcDate(latestMoj.moj_expiry_date, 'YYYY-MM-DD'))
    : null;
  const mojIsActive = mojStatus === 'active' && mojExpiry && !mojExpiry.isBefore(moment(), 'day');
  const mojIsExpired = mojExpiry && mojExpiry.isBefore(moment(), 'day');
  const calculatedMojExpiry = getMojExpiryDate();
  const expiryIsPast = calculatedMojExpiry && calculatedMojExpiry.isBefore(moment(), 'day');
  const requestDateIsFuture = mojRequestDate && moment(mojRequestDate).isAfter(moment(), 'day');

  // Build a lookup: datacentre_id → induction object
  const inductionByDc = {};
  inductions.forEach((ind) => {
    inductionByDc[ind.datacentre_id] = ind;
  });

  const getInductionStatus = (ind) => {
    if (!ind) return null;
    const expiry = ind.induction_expiry_date
      ? moment(formatUtcDate(ind.induction_expiry_date, 'YYYY-MM-DD'))
      : null;
    if (!expiry) return 'no-expiry';
    return expiry.isBefore(moment(), 'day') ? 'expired' : 'active';
  };

  // Sort permissions by customer name/id then datacentre name/id ascending
  const sortedPermissions = [...permissions].sort((a, b) => {
    const custA = (a.customer_name || String(a.customer_id)).toLowerCase();
    const custB = (b.customer_name || String(b.customer_id)).toLowerCase();
    if (custA !== custB) return custA.localeCompare(custB);
    const dcA = (a.datacentre_name || String(a.datacentre_id)).toLowerCase();
    const dcB = (b.datacentre_name || String(b.datacentre_id)).toLowerCase();
    return dcA.localeCompare(dcB);
  });

  return (
    <Grid container spacing={5} sx={{ p: 3 }}>
      {/* User Profile */}
      <Grid size={{ xs: 12, md: 6 }}>
        <StyledContainer style={{ padding: '24px', height: '100%', boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="overline" gutterBottom>
              Profile
            </Typography>
            {featuresEnabled && !profileEditing && (
              <Button size="small" onClick={startProfileEdit}>
                Edit
              </Button>
            )}
          </Box>
          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 1 }}>
              {profileSuccess}
            </Alert>
          )}
          {profileEditing ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 2,
                mt: 1,
              }}
            >
              <TextField
                label="Name *"
                size="small"
                value={profileForm.name}
                onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                inputProps={{ maxLength: CONTACT_NAME_MAX }}
              />
              <DetailRow label="Email" value={userData.email} />
              <TextField
                label="Employer"
                size="small"
                value={profileForm.employer}
                onChange={(e) => setProfileForm({ ...profileForm, employer: e.target.value })}
              />
              <TextField
                label="Role"
                size="small"
                value={profileForm.role}
                onChange={(e) => setProfileForm({ ...profileForm, role: e.target.value })}
              />
              <TextField
                label="Phone"
                size="small"
                value={profileForm.phone_number}
                onChange={(e) => setProfileForm({ ...profileForm, phone_number: e.target.value })}
                helperText="e.g. +64 9 XXX XXXX"
              />
              <TextField
                label={userData.mobile_number ? 'Mobile *' : 'Mobile'}
                size="small"
                value={profileForm.mobile_number}
                onChange={(e) => setProfileForm({ ...profileForm, mobile_number: e.target.value })}
                helperText="e.g. +6421xxxxxxx"
              />
              {profileError && (
                <Alert severity="error" sx={{ gridColumn: '1 / span 2' }}>
                  {profileError}
                </Alert>
              )}
              <Box
                sx={{
                  gridColumn: '1 / span 2',
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                }}
              >
                <Button
                  size="small"
                  onClick={() => setProfileEditing(false)}
                  disabled={profileSaving}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleProfileSave}
                  disabled={profileSaving}
                >
                  {profileSaving ? 'Saving…' : 'Save'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 2,
                mt: 1,
              }}
            >
              <DetailRow label="Name" value={userData.name} />
              <DetailRow label="Email" value={userData.email} />
              <DetailRow label="Employer" value={userData.employer} />
              <DetailRow label="Role" value={userData.role || '—'} />
              <DetailRow label="Phone" value={userData.phone_number || '—'} />
              <DetailRow label="Mobile" value={userData.mobile_number || '—'} />
            </Box>
          )}
        </StyledContainer>
      </Grid>

      {/* Contact-level MOJ Check */}
      <Grid size={{ xs: 12, md: 6 }}>
        <StyledContainer style={{ padding: '24px', height: '100%', boxSizing: 'border-box' }}>
          <Typography variant="overline" gutterBottom>
            Ministry of Justice
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
            {mojValid ? (
              <>
                <Typography variant="body2" color="text.primary">
                  Ministry of Justice Verified
                </Typography>
                {mojIsExpired ? (
                  <Chip label="Expired" color="lighterror" size="small" />
                ) : mojIsActive ? (
                  <Chip label="Active" color="lightsuccess" size="small" />
                ) : (
                  <Chip label={mojStatus || 'Not Set'} size="small" color="default" />
                )}
              </>
            ) : (
              <>
                <Typography variant="body2">No Ministry of Justice record on file</Typography>
                <Chip label="Not Set" color="default" size="small" />
              </>
            )}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2, mt: 2 }}>
            <Tooltip
              title={
                !isApprover
                  ? 'Only approvers can update the Ministry of Justice Supplier Request ID'
                  : ''
              }
              arrow
            >
              <div>
                <TextField
                  label="Ministry of Justice Supplier Request ID"
                  value={mojSupplierId}
                  onChange={(e) => {
                    const next = e.target.value;
                    const savedId = (userData.moj_supplier_id || '').trim();
                    const savedDate = userData.requested_date
                      ? formatUtcDate(userData.requested_date, 'YYYY-MM-DD')
                      : '';
                    setMojSupplierId(next);
                    // A new Request ID is a new check, so its request date must be re-entered
                    if (savedId && next.trim() !== savedId) {
                      setMojRequestDate((prev) =>
                        prev && prev.format('YYYY-MM-DD') === savedDate ? null : prev
                      );
                    } else if (savedId && next.trim() === savedId && savedDate) {
                      setMojRequestDate((prev) => prev || moment(savedDate));
                    }
                    setMojSuccess('');
                    setMojError('');
                  }}
                  helperText={MOJ_SUPPLIER_ID_HELP}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={!isApprover}
                />
              </div>
            </Tooltip>
            {/* {mojValid && (
                <DetailRow label="Updated Via" value={userData.moj_updated_platform || '—'} />
              )} */}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* MOJ Start Date + Expiry Date */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            <Tooltip
              title={
                !isApprover
                  ? 'Only approvers can update the Ministry of Justice Request Date'
                  : expiryIsPast
                    ? 'The Criminal Background Check (CBC) must have been completed within the last two years'
                    : requestDateIsFuture
                      ? 'Ministry of Justice Request Date cannot be in the future'
                      : ''
              }
              arrow
            >
              <div>
                <TextField
                  label="Ministry of Justice Request Date"
                  type="date"
                  value={mojRequestDate ? mojRequestDate.format('YYYY-MM-DD') : ''}
                  onChange={(e) => {
                    const next = e.target.value;
                    const savedDate = userData.requested_date
                      ? formatUtcDate(userData.requested_date, 'YYYY-MM-DD')
                      : '';
                    setMojRequestDate(next ? moment(next) : null);
                    // A new request date is a new check, so its Request ID must be re-entered
                    if (savedDate && next !== savedDate) {
                      setMojSupplierId((prev) =>
                        prev === (userData.moj_supplier_id || '') ? '' : prev
                      );
                    } else if (savedDate && next === savedDate) {
                      setMojSupplierId((prev) => prev || userData.moj_supplier_id || '');
                    }
                    setMojSuccess('');
                    setMojError('');
                  }}
                  helperText={MOJ_REQUEST_DATE_HELP}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  inputProps={{ max: moment().format('YYYY-MM-DD') }}
                  fullWidth
                  variant="outlined"
                  size="small"
                  disabled={!isApprover}
                />
              </div>
            </Tooltip>
            <TextField
              label="Ministry of Justice Expiry Date"
              type="date"
              value={getMojExpiryDate() ? getMojExpiryDate().format('YYYY-MM-DD') : ''}
              helperText="Automatically calculated as two years after the request date."
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              disabled
              fullWidth
              variant="outlined"
              size="small"
            />
          </Box>

          {mojError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {mojError}
            </Alert>
          )}
          {mojSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {mojSuccess}
            </Alert>
          )}

          {isApprover && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleMojSave}
                disabled={mojSaving}
              >
                {mojSaving ? 'Saving...' : 'Update Ministry of Justice'}
              </Button>
            </Box>
          )}
        </StyledContainer>
      </Grid>

      {/* Customer Portal Permissions */}
      {permissions.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <StyledContainer style={{ padding: '24px' }}>
            <Typography variant="overline" gutterBottom>
              Customer Portal Permissions
            </Typography>
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Customer</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Datacentre</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Access</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Service</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Change</strong>
                    </TableCell>
                    {!featuresEnabled && (
                      <TableCell>
                        <strong>Induction</strong>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPermissions.map((perm, index) => (
                    <TableRow key={index}>
                      <TableCell>{perm.customer_name || perm.customer_id}</TableCell>
                      <TableCell>{perm.datacentre_name || perm.datacentre_id}</TableCell>
                      <TableCell>
                        <PermChip value={perm.access_request} />
                      </TableCell>
                      <TableCell>
                        <PermChip value={perm.service_request} />
                      </TableCell>
                      <TableCell>
                        <PermChip value={perm.change_request} />
                      </TableCell>
                      {!featuresEnabled && (
                        <TableCell>
                          <InductionChip
                            induction={inductionByDc[perm.datacentre_id] || null}
                            status={getInductionStatus(inductionByDc[perm.datacentre_id])}
                            formatDate={formatDate}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledContainer>
        </Grid>
      )}

      {/* AAL List — when featuresEnabled */}
      {featuresEnabled && aalList.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <StyledContainer style={{ padding: '24px' }}>
            <Typography variant="overline" gutterBottom>
              Customer Authorised Access Lists
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Access Lists you belong to. Click a row to view contact details.
            </Typography>
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40} />
                    <TableCell>
                      <strong>Customer</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Data Centre</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Primary</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Approver</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Site Access</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Reporting</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Maintenance</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Tape Mgmt</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Commercial</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Invoicing</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aalList.map((entry, index) => (
                    <React.Fragment key={index}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedAalRow(expandedAalRow === index ? null : index)}
                      >
                        <TableCell>
                          <IconButton size="small">
                            {expandedAalRow === index ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>{entry.customer_name}</TableCell>
                        <TableCell>{entry.datacentre_name}</TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.is_primary} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.approver} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.site_access} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.reporting} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.maintenance} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.tape_management} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.billing} />
                        </TableCell>
                        <TableCell align="center">
                          <BoolChip value={entry.invoicing_contact} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={11}
                          sx={{ p: 0, borderBottom: expandedAalRow === index ? undefined : 'none' }}
                        >
                          <Collapse in={expandedAalRow === index} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                p: 2,
                                display: 'grid',
                                gridTemplateColumns: {
                                  xs: 'minmax(0, 1fr)',
                                  md: 'repeat(3, minmax(0, 1fr))',
                                },
                                gap: 2,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                    mb: 0.5,
                                    display: 'block',
                                  }}
                                >
                                  Contact Details
                                </Typography>
                                <DetailRow label="Name" value={entry.detail?.name || entry.name} />
                                <DetailRow
                                  label="Email"
                                  value={entry.detail?.email || entry.email}
                                />
                                <DetailRow label="Phone" value={entry.detail?.phone_number} />
                                <DetailRow label="Mobile" value={entry.detail?.mobile_number} />
                                <DetailRow label="Employer" value={entry.detail?.employer} />
                                <DetailRow label="Role" value={entry.detail?.role} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                    mb: 0.5,
                                    display: 'block',
                                  }}
                                >
                                  Ministry of Justice
                                </Typography>
                                <DetailRow
                                  label="Ministry of Justice Supplier Request ID"
                                  value={entry.moj?.moj_supplier_id}
                                />
                                <DetailRow
                                  label="Ministry of Justice Request Date"
                                  value={
                                    entry.moj?.moj_start_date
                                      ? formatUtcDate(entry.moj.moj_start_date, 'DD MMM YYYY')
                                      : '—'
                                  }
                                />
                                <DetailRow
                                  label="Ministry of Justice Expiry Date"
                                  value={
                                    entry.moj?.moj_expiry_date
                                      ? formatUtcDate(entry.moj.moj_expiry_date, 'DD MMM YYYY')
                                      : '—'
                                  }
                                />
                                <DetailRow label="Status" value={entry.moj?.status} />
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                    mb: 0.5,
                                    display: 'block',
                                  }}
                                >
                                  Induction
                                </Typography>
                                {entry.induction ? (
                                  <>
                                    <DetailRow
                                      label="Data Centre"
                                      value={entry.induction.datacentre_name}
                                    />
                                    <DetailRow
                                      label="Start"
                                      value={
                                        entry.induction.induction_start_date
                                          ? formatUtcDate(
                                              entry.induction.induction_start_date,
                                              'DD MMM YYYY'
                                            )
                                          : '—'
                                      }
                                    />
                                    <DetailRow
                                      label="Expiry"
                                      value={
                                        entry.induction.induction_expiry_date
                                          ? formatUtcDate(
                                              entry.induction.induction_expiry_date,
                                              'DD MMM YYYY'
                                            )
                                          : '—'
                                      }
                                    />
                                  </>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    No induction record
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledContainer>
        </Grid>
      )}
    </Grid>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" color="text.primary" sx={{ overflowWrap: 'anywhere' }}>
      {value || '—'}
    </Typography>
  </Box>
);

const PermChip = ({ value }) => {
  if (!value || value === 'None') return <Chip label="None" size="small" color="default" />;
  if (value === 'Can Raise') return <Chip label="Can Raise" size="small" color="lightsuccess" />;
  if (value === 'View Only') return <Chip label="View Only" size="small" color="accent3" />;
  return <Chip label={value} size="small" />;
};

const BoolChip = ({ value }) => (
  <Chip
    label={value ? 'Yes' : 'No'}
    size="small"
    color={value ? 'lightsuccess' : 'default'}
    sx={{ minWidth: 36 }}
  />
);

const InductionChip = ({ induction, status, formatDate }) => {
  if (!induction || !status) {
    return <Chip label="No Induction" size="small" color="default" />;
  }

  const expiryStr = induction.induction_expiry_date
    ? formatUtcDate(induction.induction_expiry_date, 'DD MMM YYYY')
    : null;

  if (status === 'active') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip label="Active" size="small" color="lightsuccess" sx={{ width: 'fit-content' }} />
        {expiryStr && (
          <Typography variant="caption" color="text.secondary">
            Expires {expiryStr}
          </Typography>
        )}
      </Box>
    );
  }

  if (status === 'expired') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip label="Expired" size="small" color="lighterror" sx={{ width: 'fit-content' }} />
        {expiryStr && (
          <Typography variant="caption" color="text.secondary">
            Expired {expiryStr}
          </Typography>
        )}
      </Box>
    );
  }

  // no-expiry or other
  return <Chip label="No Expiry Set" size="small" color="lightwarning" />;
};

export default UserDetails;
